import { useState } from "react";
import DatePicker from "react-datepicker";
import { useCompany } from "../components/CompanySelector";
import { Partner, Product } from "../dto/commons";
import type { PurchaseRow } from "../dto/purchases.dto";
import {
  exportPurchaseReport,
  getPurchaseReport,
  GetPurchaseReportQuery,
} from "../services/purchases.service";
import DataTable, { type Column } from "../components/DataTable";
import { formatCurrency, formatDate } from "../utils/helpers";
import { EmptyState, ErrorState } from "../components/Illustrations";
import useSWR from "swr";
import type { Branch } from "../dto/commons";
import { BranchSelector } from "../components/selector/BranchSelector";
import ProductSelector from "../components/selector/ProductSelector";
import { PartnerSelector } from "../components/selector/PartnerSelector";
import { cn } from "../utils/style";
import Select from "react-select";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { getStartOfMonth } from "../utils/helpers";

const columns: Column<PurchaseRow>[] = [
  {
    accessorKey: "id",
    header: "Reference",
    defaultHidden: true,
    id: "id",
  },
  {
    accessorKey: "invoice_name",
    header: "Invoice Number",
  },
  {
    accessorFn: (row) =>
      row.invoice_date
        ? new Date(row.invoice_date).toLocaleDateString("id-ID")
        : "-",
    header: "Invoice Date",
  },
  {
    header: "Account",
    accessorKey: "account",
    minSize: 150,
  },
  {
    accessorKey: "vendor",
    header: "Vendor",
    minSize: 150,
  },
  {
    accessorKey: "branch",
    header: "Branch",
    minSize: 200,
  },
  {
    accessorKey: "buyer",
    header: "Buyer",
    minSize: 200,
  },
  {
    accessorKey: "responsible_buyer",
    header: "Responsible Buyer",
    minSize: 200,
  },
  {
    accessorFn: (row) =>
      `${row.product}` +
      (row.product_attributes ? ` (${row.product_attributes})` : ""),
    header: "Product",
    minSize: 200,
  },
  {
    accessorKey: "category",
    header: "Category",
    defaultHidden: true,
    id: "category",
  },
  {
    accessorKey: "uom",
    header: "UoM",
    defaultHidden: true,
    id: "uom",
  },
  {
    accessorFn: (row) =>
      row.quantity ? `${Number.parseInt(row.quantity)}` : "0",
    header: "Qty",
  },
  {
    header: "Price Unit",
    cell: ({ row }) => (
      <div className="font-semibold">
        {formatCurrency(Number.parseFloat(row.original.price_unit || "0"))}
      </div>
    ),
  },
  {
    header: "Subtotal",
    cell: ({ row }) => (
      <div className="font-semibold">
        {formatCurrency(
          Number.parseFloat(row.original.price_unit || "0") *
            Number.parseFloat(row.original.quantity || "0"),
        )}
      </div>
    ),
  },
  {
    header: "Type",
    cell: ({ row }) => (
      <div
        className={cn(
          "text-xs font-medium rounded-full px-2 py-1",
          row.original.move_type === "in_invoice" && "bg-red-200",
          row.original.move_type === "in_refund" && "bg-green-200",
        )}
      >
        {row.original.move_type === "in_invoice" ? "Invoice" : "Refund"}
      </div>
    ),
  },
  {
    header: "Serial Numbers",
    cell: ({ row }) => (
      <div className="flex flex-wrap items-center gap-2">
        {row.original.serial_numbers?.map((sl) => (
          <div className="text-xs font-medium rounded-full px-2 py-1 bg-gray-200 border-gray-300 text-gray-800">
            {sl}
          </div>
        ))}
      </div>
    ),
    defaultHidden: true,
    id: "serial_numbers",
  },
  {
    accessorKey: "memo",
    defaultHidden: true,
    header: "Memo",
    id: "memo",
  },
  {
    accessorKey: "note",
    defaultHidden: true,
    header: "Note",
    id: "note",
  },
];

type MoveType = "in_invoice" | "in_refund";

export default function PurchaseReportPage() {
  const { selectedCompany } = useCompany();

  const [startDate, setStartDate] = useState<Date | null>(getStartOfMonth());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedBranches, setSelectedBranches] = useState<Branch[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<Partner[]>([]);
  const [selectedType, setSelectedType] = useState<MoveType | undefined>(
    undefined,
  );
  const [paginationState, setPaginationState] = useState<{
    pageSize: number;
    pageIndex: number;
  }>({
    pageSize: 10,
    pageIndex: 0,
  });

  const [exporting, setExporting] = useState(false);
  const [isFetched, setFetched] = useState(false);
  const { data, isLoading, error, mutate } = useSWR(
    isFetched && selectedCompany && startDate && endDate
      ? [
          "/reports/purchases",
          {
            page: paginationState.pageIndex + 1,
            limit: paginationState.pageSize,
            companyId: selectedCompany.id,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            branchIds: selectedBranches.map((b) => b.id),
            vendorIds: selectedVendors.map((v) => v.id),
            productIds: selectedProducts.map((p) => p.id),
            type: selectedType,
          } as GetPurchaseReportQuery,
        ]
      : null,
    ([_, query]) => getPurchaseReport(query),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const fetchData = async () => {
    setFetched(true);
  };

  const typeOptions = [
    { value: "in_invoice", label: "Invoice" },
    { value: "in_refund", label: "Refund" },
  ];

  const handleExport = async () => {
    try {
      setExporting(true);
      if (selectedCompany && startDate && endDate) {
        await exportPurchaseReport({
          companyId: selectedCompany.id,
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
          branchIds: selectedBranches.map((b) => b.id),
          vendorIds: selectedVendors.map((v) => v.id),
          productIds: selectedProducts.map((p) => p.id),
          type: selectedType,
        });
      }
    } catch (e) {
      alert("Something went wrong went exporting data");
      console.log(e);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    startDate,
    endDate,
    selectedBranches,
    selectedProducts,
    selectedVendors,
    selectedType,
  ]);

  return (
    <>
      <h2 className="text-3xl font-bold mb-4 text-gray-800">Purchase Report</h2>
      <div className="bg-white p-4 rounded-md border border-gray-200 grid md:grid-cols-12 gap-4 mb-4 items-start">
        <div className="md:col-span-2 *:w-full">
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            Start Date
          </label>
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date)}
            dateFormat="dd MMM yyyy"
            placeholderText="Select Date"
            className="mt-1 block w-full rounded-[4px] border border-gray-200 focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>

        <div className="md:col-span-2 *:w-full">
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            End Date
          </label>
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date)}
            dateFormat="dd MMM yyyy"
            placeholderText="Select Date"
            className="mt-1 block w-full rounded-[4px] border border-gray-200 focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>

        <div className="md:col-span-2">
          <label
            htmlFor="typeSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Type
          </label>
          <Select
            id="typeSelector"
            placeholder="All Type"
            value={typeOptions.find((opt) => opt.value === selectedType)}
            options={typeOptions}
            onChange={(selectedOption) =>
              setSelectedType((selectedOption?.value as MoveType) || undefined)
            }
            isClearable
            className="mt-1"
          />
        </div>

        <div className="md:col-span-2">
          <label
            htmlFor="branchSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Branch
          </label>
          <BranchSelector
            id="branchSelector"
            value={selectedBranches}
            onChange={setSelectedBranches}
          />
        </div>

        <div className="md:col-span-2">
          <label
            htmlFor="vendorSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Vendor
          </label>
          <PartnerSelector
            id="vendorSelector"
            placeholder="All Vendors"
            type="supplier"
            value={selectedVendors}
            onChange={setSelectedVendors}
          />
        </div>

        <div className="md:col-span-2">
          <label
            htmlFor="productSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Product
          </label>
          <ProductSelector
            selectedProducts={selectedProducts}
            onChange={setSelectedProducts}
          />
        </div>
      </div>

      <div className="bg-white rounded-md border border-gray-200 border-collapse">
        {error ? (
          <div className="min-h-[40vh] flex flex-col items-center justify-center gap-2 p-4">
            <ErrorState className="h-40 w-auto" />
            <p className="text-gray-500 text-center">
              Oops.. Something went wrong <br />
              Try generate the report again
            </p>

            <button
              type="button"
              onClick={() => mutate()}
              className="mt-4 disabled:cursor-not-allowed bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={!selectedCompany || !startDate || !endDate || isLoading}
            >
              {isLoading ? "Reloading..." : "Reload"}
            </button>
          </div>
        ) : !isFetched ? (
          <div className="min-h-[40vh] flex flex-col items-center justify-center gap-2 p-4">
            <EmptyState className="h-40 w-auto" />
            <p className="text-gray-500 text-center">
              No data available for the selected criteria. <br />
              Please generate the report.
            </p>

            <button
              type="button"
              onClick={fetchData}
              className="mt-4 disabled:cursor-not-allowed bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={!selectedCompany || !startDate || !endDate || isLoading}
            >
              {isLoading ? "Generating..." : "Generate"}
            </button>
          </div>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={data?.data || []}
              pagination={paginationState}
              rowCount={data?.meta.total_row || 0}
              onPaginationChange={setPaginationState}
              loading={isLoading}
              className="rounded-none border-x-0"
              toolbars={({ table }) => (
                <div className="pt-4 px-4 pb-2 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-600">
                    Showing{" "}
                    <span className="text-gray-800">{table.getRowCount()}</span>{" "}
                    results
                  </div>
                  <div className="flex items-center gap-2">
                    {data?.data && data.data.length > 0 && (
                      <button
                        type="button"
                        disabled={
                          exporting ||
                          !selectedCompany ||
                          !startDate ||
                          !endDate
                        }
                        onClick={handleExport}
                        className={cn(
                          "flex items-center justify-center gap-2 h-10 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium",
                          "disabled:bg-green-600/40 disabled:cursor-not-allowed",
                          exporting && "cursor-progress",
                        )}
                      >
                        <ArrowDownTrayIcon className="size-4" />
                        {exporting ? "Exporting..." : "Export"}
                      </button>
                    )}
                    <DataTable.ColumnToggle />
                  </div>
                </div>
              )}
            />
          </>
        )}
      </div>
    </>
  );
}
